import React, { useState, useEffect } from "react";
import { Card, ListGroup, Form, Dropdown } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "./GenericFHIRViewerModal";
import { fetchPatient } from "../services/patientService";
import { useDispatch, useStore } from "react-redux";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import tooltipRegistry from "../tooltips/tooltipRegistry";

// ✅ Import gender-specific icons
import PatientFemaleIcon from "../assets/Patient_Female_96.png";
import PatientMaleIcon from "../assets/Patient_Male_96.png";

function PatientCard({ title, inContext, patientData, fhirClient, showSearch = false, onSelectPatient, isReply }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchedPatients, setSearchedPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(patientData || null);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalPatientDetails, setModalPatientDetails] = useState(null);
  const dispatch = useDispatch();
  const store = useStore();

  useEffect(() => {
    if (patientData?.name?.[0]) {
      const name = `${patientData.name[0].given?.join(" ") || ""} ${patientData.name[0].family || ""}`.trim();
      setSearchTerm(name);
    }
  }, [patientData]);

  const displayPatient = selectedPatient || patientData || {
    id: "N/A",
    name: [{ given: ["N/A"], family: "N/A" }],
    gender: "N/A",
    birthDate: "N/A",
    address: [{ line: ["N/A"], city: "N/A", state: "N/A", postalCode: "N/A" }],
    maritalStatus: { text: "N/A" },
    communication: [{ language: { text: "N/A" } }],
    telecom: [{ system: "phone", value: "N/A" }]
  };

  const handleModalClose = () => setShowModal(false);

  const handleModalOpen = async () => {
    if (!displayPatient.id) {
      console.warn("⚠️ Patient ID not available.");
      setModalPatientDetails(null);
      setShowModal(true);
      return;
    }

    const patient = await fetchPatient(displayPatient.id, fhirClient, store.getState, dispatch);
    setModalPatientDetails(patient || null);
    setShowModal(true);
  };

  const handleSearchByName = async () => {
    if (!searchTerm.trim()) return setError("Please enter a patient name.");
    if (!fhirClient) return setError("FHIR Client is not initialized.");

    try {
      const searchResults = await fhirClient.request(`Patient?name=${encodeURIComponent(searchTerm)}`);
      const found = searchResults.entry?.map(e => e.resource) || [];
      setSearchedPatients(found);
      setError(found.length ? "" : "No patients found.");
    } catch (err) {
      console.error("Error searching patient:", err);
      setError("Error searching patient.");
    }
  };

  const handleSelectPatient = async (patient) => {
    const fullName = patient.name?.[0]?.given?.join(" ") + " " + patient.name?.[0]?.family;
    setSearchTerm(fullName);

    const fetched = await fetchPatient(patient.id, fhirClient, store.getState, dispatch);
    const resolvedPatient = fetched || patient;

    setSelectedPatient(resolvedPatient);
    setSearchedPatients([]);

    if (typeof onSelectPatient === "function") {
      onSelectPatient(resolvedPatient);
    }
  };

  const getPatientName = (patient) => {
    const nameObj = patient?.name?.[0];
    if (!nameObj) return "Patient";
    const given = nameObj.given?.join(" ") || "";
    const family = nameObj.family || "";
    return `${given} ${family}`.trim();
  };

  const patientAddress = displayPatient.address?.[0];
  const fullAddress = [
    ...(patientAddress?.line || []),
    patientAddress?.city,
    patientAddress?.state,
    patientAddress?.postalCode
  ].filter(Boolean).join(", ");

  const getMRN = (patient) => {
    const identifierArray = patient.identifier || [];
    const mrnIdentifier = identifierArray.find((id) =>
      id.type?.coding?.some((coding) => coding.code === "MR")
    );
    return mrnIdentifier?.value || "MRN not found";
  };

  const mrn = getMRN(displayPatient);

  // ✅ Select gender-specific icon
  const gender = displayPatient.gender?.toLowerCase();
  const patientIcon = gender === "female" ? PatientFemaleIcon : PatientMaleIcon;

  return (
    <>
      <Card style={{ width: "100%", marginTop: "5px", minHeight: "275px" }}>
        <Card.Header className={`d-flex justify-content-between align-items-center ${inContext ? styles.inContextCardHeader : styles.cardHeader}`}>
          <h5 className="mb-0">{title}</h5>

          <OverlayTrigger placement="bottom" overlay={tooltipRegistry.patientCard}>
            <img
              src={patientIcon}
              alt="Patient Icon"
              style={{ cursor: "pointer", height: "32px", width: "32px" }}
              onClick={handleModalOpen}
            />
          </OverlayTrigger>
        </Card.Header>

        <Card.Body>
          {error && <p style={{ color: "red" }}>{error}</p>}
          <ListGroup variant="flush">
            <ListGroup.Item className={`d-flex align-items-center ${styles.listItem}`}>
              <strong>Name:&nbsp;</strong>
              {showSearch ? (
                <div className="d-flex align-items-center ms-2" style={{ flex: 1, position: "relative" }}>
                  <Form.Control
                    type="text"
                    placeholder="Find a Patient"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (!isReply) handleSearchByName();
                      }
                    }}
                    className="me-2"
                    disabled={isReply}
                  />
                  <FaSearch
                    style={{
                      cursor: isReply ? "not-allowed" : "pointer",
                      color: isReply ? "#6c757d" : "#007bff",
                      marginLeft: "5px"
                    }}
                    onClick={() => {
                      if (!isReply) handleSearchByName();
                    }}
                  />
                  {searchedPatients.length > 0 && (
                    <Dropdown.Menu
                      show
                      style={{
                        position: "absolute",
                        width: "calc(100% + 150px)",
                        top: "100%",
                        left: 0,
                        zIndex: 1000,
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #ced4da",
                        boxShadow: "0px 4px 10px rgba(0,0,0,0.1)"
                      }}
                    >
                      {searchedPatients.map((patient) => (
                        <Dropdown.Item key={patient.id} onClick={() => handleSelectPatient(patient)}>
                          <div>
                            <div><strong>{patient.name?.[0]?.given?.join(" ")} {patient.name?.[0]?.family}</strong></div>
                            <div className="text-muted small">
                              {patient.gender?.charAt(0).toUpperCase() + patient.gender?.slice(1) || "Unknown"} · Birth Date: {patient.birthDate || "N/A"}
                            </div>
                            <div className="text-muted small">
                              {patient.address?.[0]?.city}, {patient.address?.[0]?.state}
                            </div>
                          </div>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  )}
                </div>
              ) : (
                <>&nbsp;{displayPatient.name?.[0]?.given?.join(" ")} {displayPatient.name?.[0]?.family}</>
              )}
            </ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>MRN:</strong> {mrn}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Gender:</strong> {displayPatient.gender}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Birth Date:</strong> {displayPatient.birthDate}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Marital Status:</strong> {displayPatient.maritalStatus?.text}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Address:</strong> {fullAddress || "N/A"}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Language Spoken:</strong> {displayPatient.communication?.[0]?.language?.text}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Phone Number:</strong> {displayPatient.telecom?.find(t => t.system === "phone")?.value}</ListGroup.Item>
          </ListGroup>
        </Card.Body>
      </Card>

      <GenericFHIRViewerModal
        show={showModal}
        onClose={handleModalClose}
        resource={modalPatientDetails}
        title={getPatientName(patientData)}

      />
    </>
  );
}

export default PatientCard;